import React, { forwardRef } from 'react';
import { Box, Flex, FlexProps, TextV2 } from '@withjoy/joykit';
import { EditorSortableItemLayout } from '@apps/admin/components/EditorSortableItemLayout';
import { SkeletonText } from '@shared/components/Skeleton';
import { AccommodationCard, TravelMapBlockType } from '../../Accommodations.types';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { CardTag } from '../CardTag';
import { StarRating } from '@shared/components';
import { useTranslation } from '@shared/core';
import { RoomBlockDate } from '../RoomBlockDate';
import { HotelTags } from '../HotelTags';
import { RoomBlockPrice } from '../RoomBlockPrice';
import { RoomsIcon } from '@assets/icons';
import { HotelPrice } from '../HotelPrice';
import { ActionMenu } from '../ActionMenu';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { pxToRem } from '@withjoy/joykit/theme';
import { DragIcon } from '@assets/icons';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { TagsOnThumbnail, Thumbnail } from '../Thumbnail';
import { AutoPopulateInsight } from '../AutoPopulateInsight';
import { HotelTourStep } from '../AccommodationsTour';
import { HotelRatingV2 } from '@shared/components/HotelRatingV2';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface SortableCardProps
  extends Readonly<{
    hotel: AccommodationCard;
    hotelIndex: number;
    isDragActive: boolean;
    onHotelClick: (hotel: AccommodationCard) => void;
    showAutoPopulateInsights: boolean;
    // Explicit container width in pixels to maintain consistent card width during drag-and-drop.
    // When null, falls back to width="100%". Required because DragOverlay portals to document.body
    // where percentage widths lose their parent context.
    containerWidth?: number | null;
    isItemDragActive?: boolean;
    isFirstTimeAutoPopulation: boolean;
  }> {}

const SortableCard = (props: SortableCardProps) => {
  const { hotel, hotelIndex, isDragActive, onHotelClick, showAutoPopulateInsights, containerWidth, isItemDragActive, isFirstTimeAutoPopulation } = props;

  const isMobile = useIsMobileScreen();
  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();
  const { accommodationPropertyPhotos } = useAccommodationsContext();

  const [accommodationsCustomPropertyPhotoEnabled] = useIsFeatureEnabled('accommodationsCustomPropertyPhoto');

  const hasRoomBlock = hotel.type === TravelMapBlockType.travelMapAccommodationsRoomblock;
  const hasHotel = hotel.type === TravelMapBlockType.travelMapAccommodationsHotel;
  const isDisabledDrag = Boolean(hotel.isFavorite);
  const accommodationPhotos = accommodationPropertyPhotos?.searchAccommodationProperties?.properties
    ?.find(photo => photo.joyPlaceId === hotel.joyPlaceId)
    ?.photos?.map(photo => ({ src: photo.url, alt: photo.url }));

  const { t } = useTranslation('websiteEditor');
  const hotelTrans = t('customPage', 'travelMap', 'hotel');
  const starRatingTooltipTrans = t('customPage', 'itemTypes', 'travelMapAccommodationsHotel', 'starRatingTooltip');

  const autoPopulateInsight = hotel.autoSuggestionRecommendation
    ? {
        whyThisHotelIsAGreatPick: hotel.autoSuggestionRecommendation.whyThisHotelIsAGreatPick,
        proposedNoteFromHostToGuest: hotel.autoSuggestionRecommendation.proposedNoteFromHostToGuest,
      }
    : null;

  const shouldShowAutoPopulateInsight =
    showAutoPopulateInsights && autoPopulateInsight && (autoPopulateInsight.whyThisHotelIsAGreatPick || autoPopulateInsight.proposedNoteFromHostToGuest);

  const shouldShowThumbnail = hasHotel || hasRoomBlock || (accommodationsCustomPropertyPhotoEnabled && !hotel.isPhotoHidden && hotel.photoUrl);

  return (
    <CardContainer
      role="group"
      onClick={() => onHotelClick(hotel)}
      cursor="pointer"
      containerWidth={containerWidth}
    >
      <DragHandle
        visibility={(isDragActive || isMobile) && !isDisabledDrag ? 'visible' : 'hidden'}
        cursor={isDragActive ? 'grabbing' : 'grab'}
        {...attributes}
        {...listeners}
        ref={draggableNodeRef}
      >
        <DragIcon
          size={16}
          style={{ transform: 'rotate(90deg)' }}
        />
      </DragHandle>
      <HotelTourStep
        hotel={hotel}
        hotelIndex={hotelIndex}
      >
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          backgroundColor="white"
          paddingX={4}
          paddingY={3}
          borderRadius={pxToRem(12)}
        >
          <Flex
            flexDirection="column"
            gap={3}
          >
            <CardTag
              isFavorite={hotel.isFavorite}
              type={hotel.type}
            />
            <TextEllipsisWrapper
              lineClamp={2}
              typographyVariant="button2"
            >
              {hotel.displayName ?? ''}
            </TextEllipsisWrapper>
            {!(hasHotel || hasRoomBlock) && hotel.address && (
              <TextEllipsisWrapper
                lineClamp={2}
                typographyVariant="body1"
                color={'mono10'}
              >
                {hotel.address}
              </TextEllipsisWrapper>
            )}
            {(hasHotel || hasRoomBlock) && (
              <Flex
                gap={5}
                alignItems="center"
                flexWrap="wrap"
              >
                {!!hotel?.starRating && (
                  <StarRating
                    id={`star-rating-${hotel.id}`}
                    showPartialRating
                    rating={hotel.starRating}
                    showTooltip={true}
                    toolTipContent={
                      <Flex maxWidth={pxToRem(256)}>
                        <Box typographyVariant="label3">
                          {starRatingTooltipTrans.title()}
                          <Box
                            typographyVariant="label2"
                            color="mono4"
                            display="inline"
                          >
                            {starRatingTooltipTrans.content()}
                          </Box>
                        </Box>
                      </Flex>
                    }
                  />
                )}
                <DistanceFromVenue
                  distances={hotel.distances}
                  milesToVenue={hotel.distance}
                  hasIcon
                />
                {hotel.numberOfRoomsReserved && (
                  <Flex
                    alignItems="center"
                    gap={2}
                    typographyVariant="body1"
                    color="mono10"
                  >
                    <RoomsIcon />
                    {hotelTrans.rooms({ rooms: hotel.numberOfRoomsReserved })}
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
          <ActionMenu hotel={hotel} />
        </Flex>
      </HotelTourStep>
      {!isItemDragActive && (
        <>
          {!hotel.isCustomLinkEnabled && hotel.joyPlaceId && (
            <HotelRatingV2
              paddingX={4}
              marginTop={1}
              marginBottom={3}
              variant="leftAligned"
              hotel={{ id: hotel.id, joyPlaceId: hotel.joyPlaceId, zhId: hotel.placeId, type: hotel.type }}
            />
          )}
          <Flex
            flexDirection="column"
            marginTop={3}
            paddingX={4}
            gap={5}
          >
            {hasRoomBlock && (
              <RoomBlockDate
                checkInDate={hotel?.checkInDate}
                checkOutDate={hotel?.checkOutDate}
                cutoffDate={hotel?.cutoffDate}
              />
            )}
            {shouldShowThumbnail && (
              <Thumbnail
                hotel={hotel}
                accommodationPhotos={accommodationPhotos ?? []}
                renderTags={() => (
                  <TagsOnThumbnail
                    isFirstTimeAutoPopulation={isFirstTimeAutoPopulation}
                    hideTravelTime={false}
                    venueGooglePlaceId={hotel.autoSuggestionRecommendation?.venueGooglePlaceId}
                    distances={hotel.distances}
                    milesToVenue={hotel.distance}
                  />
                )}
              />
            )}

            <HotelTags hotel={hotel} />
            {shouldShowAutoPopulateInsight && autoPopulateInsight && <AutoPopulateInsight insight={autoPopulateInsight} />}
            {hasRoomBlock ? (
              <RoomBlockPrice
                price={hotel?.price || 0}
                strikeoutPrice={hotel?.strikeoutPrice || 0}
                noOfRooms={hotel?.numberOfRoomsReserved || 0}
                currency={hotel?.currency}
              />
            ) : (
              hasHotel &&
              hotel.isFromAccommodationCatalog && (
                <HotelPrice
                  accommodation={hotel}
                  showComparativePrice={true}
                />
              )
            )}
          </Flex>
        </>
      )}
    </CardContainer>
  );
};
interface CustomFlexProps extends FlexProps {
  children: React.ReactNode;
  containerWidth?: number | null;
}

export const CardContainer = (props: CustomFlexProps) => (
  <Flex
    flexDirection="column"
    borderRadius={pxToRem(12)}
    border="1px solid"
    borderColor="mono3"
    paddingX={4}
    paddingTop={4}
    paddingBottom={6}
    backgroundColor="white"
    // Use explicit pixel width when provided (during drag-and-drop), otherwise default to 100%.
    // This ensures consistent width when the card is portaled to document.body via DragOverlay.
    width={props.containerWidth ? pxToRem(props.containerWidth) : '100%'}
    minWidth="fit-content"
    position="relative"
    _hover={{
      boxShadow: '0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)',
    }}
    {...props}
  />
);

export const DragHandle = forwardRef<HTMLDivElement, CustomFlexProps>((props, ref) => (
  <Flex
    width={pxToRem(40)}
    height={pxToRem(40)}
    left={0}
    top="50%"
    transform="translate(-50%, -50%)"
    borderRadius="50%"
    alignItems="center"
    justifyContent="center"
    border="1px solid"
    borderColor="mono3"
    position="absolute"
    backgroundColor="white"
    _groupHover={{ visibility: 'visible' }}
    ref={ref}
    {...props}
  />
));

// @todo - Update skeleton
const SortableCardSkeleton = () => {
  return (
    <EditorSortableItemLayout
      itemId="0"
      isDragActive={false}
      isDisabledDrag={true}
      dragItemId="0"
      isLastItem={true}
      // height={itemSize}
      isHover={false}
      borderSpacing={48}
      setIsHover={() => {}}
      hideUpdate={true}
      hideDelete={true}
    >
      <Flex
        flexDirection="column"
        rowGap={1}
      >
        <TextV2
          typographyVariant="button2"
          color={'mono14'}
          _groupActive={{ color: 'linkActive' }}
        >
          <SkeletonText
            rows={1}
            borderRadius={3}
            width={170}
            height={22}
            skeletonHeight={'100%'}
          />
        </TextV2>
      </Flex>
    </EditorSortableItemLayout>
  );
};

export { SortableCard, SortableCardSkeleton };
